import Lang from './lang'

const APPLICATION_STEPS = [
  { key: 'Applied', labelKey: 'applied' },
  { key: 'Screening', labelKey: 'screening' },
  { key: 'Assessment', labelKey: 'assessment' },
  { key: 'Interview HC', labelKey: 'interview_hc' },
  { key: 'Interview User', labelKey: 'interview_user' },
  { key: 'Medical Check Up', labelKey: 'medical_check_up' },
  { key: 'Job Offer', labelKey: 'job_offer' },
]

const STEP_PRESENTATION_MAP = {
  done: {
    bubbleClassName: 'bg-[#22C55E]',
    textClassName: 'text-[#22C55E]',
  },
  rejected: {
    bubbleClassName: 'bg-[#EA2427]',
    textClassName: 'text-[#EA2427]',
  },
  active: {
    bubbleClassName: 'bg-[#FFC107]',
    textClassName: 'text-[#FFC107]',
  },
  pending: {
    bubbleClassName: 'bg-[#E5E7EB]',
    textClassName: 'text-[#6B7280]',
  },
}

const normalizeStatusValue = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
const isNullish = (value) => value === null || value === undefined
const getDateTime = (value) => {
  if (!value) return null

  const parsedDate = new Date(value)

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const isExpiredDateTime = (value) => {
  const parsedDate = getDateTime(value)

  return parsedDate instanceof Date && parsedDate.getTime() < Date.now()
}

const getBaseApplicationStatus = (job) => {
  const applicationStatus = normalizeStatusValue(job?.status)
  const isPassPretest = job?.isPassPretest ?? job?.IsPassPretest
  const psychotestLink = String(
    job?.psychotestLink ?? job?.PsychotestLink ?? '',
  ).trim()
  const assessmentTestLink = String(
    job?.assessmentTestLink ?? job?.AssessmentTestLink ?? '',
  ).trim()
  const hasAssessmentStage = psychotestLink !== '' || assessmentTestLink !== ''
  const hasMedicalCheckUpStage =
    job?.jobIsMedicalCheckUp === true || job?.JobIsMedicalCheckUp === true
  const isPretestPending =
    job?.isScreening === true &&
    job?.isScreeningUser === true &&
    job?.hasPretest === true &&
    isNullish(isPassPretest)
  const hasScreeningState =
    !isNullish(job?.isScreening) ||
    !isNullish(job?.isScreeningUser) ||
    applicationStatus === 'screening'

  if (
    applicationStatus === 'offering' ||
    applicationStatus === 'job offer' ||
    !isNullish(job?.isOffer)
  ) {
    return 'Job Offer'
  }

  if (hasMedicalCheckUpStage && !isNullish(job?.isMedicalCheckUp)) {
    return 'Medical Check Up'
  }

  if (hasMedicalCheckUpStage && job?.isInterviewUser === true) {
    return 'Medical Check Up'
  }

  if (!isNullish(job?.isInterviewUser)) {
    return 'Interview User'
  }

  if (job?.isInterview === true) {
    return 'Interview User'
  }

  if (!isNullish(job?.isInterview) || applicationStatus === 'interview') {
    return 'Interview HC'
  }

  if (job?.isAssessment === true) {
    return 'Interview HC'
  }

  if (isPretestPending) {
    return 'Screening'
  }

  if (
    hasAssessmentStage &&
    (!isNullish(job?.isAssessment) || applicationStatus === 'assessment')
  ) {
    return 'Assessment'
  }

  if (applicationStatus === 'assessment' && !hasAssessmentStage) {
    return 'Interview HC'
  }

  if (hasScreeningState) {
    return 'Screening'
  }

  return 'Applied'
}

export const isPretestExpired = (job) => {
  const isPassHardFilter = job?.isPassHardFilter ?? job?.IsPassHardFilter
  const isPassPretest = job?.isPassPretest ?? job?.IsPassPretest
  const hasPretest = job?.hasPretest ?? job?.HasPretest ?? false
  const pretestExpiredAt = job?.pretestExpiredAt ?? job?.PretestExpiredAt

  return (
    getBaseApplicationStatus(job) === 'Screening' &&
    job?.isScreening !== false &&
    isPassHardFilter === true &&
    hasPretest === true &&
    isNullish(isPassPretest) &&
    isExpiredDateTime(pretestExpiredAt)
  )
}

export const isAssessmentExpired = (job) => {
  const psychotestLink = String(
    job?.psychotestLink ?? job?.PsychotestLink ?? '',
  ).trim()
  const assessmentTestLink = String(
    job?.assessmentTestLink ?? job?.AssessmentTestLink ?? '',
  ).trim()
  const hasAssessmentStage = psychotestLink !== '' || assessmentTestLink !== ''

  return (
    hasAssessmentStage &&
    getBaseApplicationStatus(job) === 'Assessment' &&
    isNullish(job?.isAssessment) &&
    isExpiredDateTime(job?.assessmentDate)
  )
}

export const isInterviewExpired = (job) => {
  const currentStatus = getBaseApplicationStatus(job)
  const invitationStatus = normalizeStatusValue(job?.interviewInvitationStatus)
  const interviewInvitationExpiredAt =
    job?.interviewInvitationExpiredAt || job?.bookingMeta?.expiredAt || ''
  const isReschedule =
    job?.interviewInvitationIsReschedule === true ||
    job?.bookingMeta?.isReschedule === true
  const hasScheduledInterview = Boolean(
    job?.interviewDate ||
      job?.interviewUserDate ||
      job?.interview?.selectedDateTime,
  )

  return (
    !isReschedule &&
    (currentStatus === 'Interview HC' || currentStatus === 'Interview User') &&
    !hasScheduledInterview &&
    (invitationStatus === 'expired' ||
      isExpiredDateTime(interviewInvitationExpiredAt))
  )
}

export const isJobOfferExpired = (job) => {
  const currentStatus = getBaseApplicationStatus(job)
  const jobOfferStatus = normalizeStatusValue(job?.jobOffer?.status)
  const offerExpiredAt =
    job?.jobOffer?.offerExpiredAt ||
    job?.jobOffer?.OfferExpiredAt

  return (
    currentStatus === 'Job Offer' &&
    (jobOfferStatus === 'offerexpired' ||
      jobOfferStatus === 'preonboardingexpired' ||
      isExpiredDateTime(offerExpiredAt))
  )
}

export const isApplicationCompleted = (job) =>
  normalizeStatusValue(job?.jobOffer?.status) === 'hired' ||
  job?.isOffer === true ||
  (job?.isScreening === true &&
    job?.isScreeningUser === true &&
    job?.isAssessment === true &&
    job?.isInterview === true &&
    job?.isInterviewUser === true &&
    job?.isMedicalCheckUp === true &&
    job?.isOffer === true)

export const isApplicationRejected = (job) => {
  const hasMedicalCheckUpStage =
    job?.jobIsMedicalCheckUp === true || job?.JobIsMedicalCheckUp === true

  return (
    isPretestExpired(job) ||
    isAssessmentExpired(job) ||
    isInterviewExpired(job) ||
    isJobOfferExpired(job) ||
    job?.isPassPretest === false ||
    job?.IsPassPretest === false ||
    job?.isScreening === false ||
    job?.isScreeningUser === false ||
    ((String(job?.psychotestLink ?? job?.PsychotestLink ?? '').trim() !== '' ||
      String(
        job?.assessmentTestLink ?? job?.AssessmentTestLink ?? '',
      ).trim() !== '') &&
      job?.isAssessment === false) ||
    job?.isInterview === false ||
    job?.isInterviewUser === false ||
    (hasMedicalCheckUpStage && job?.isMedicalCheckUp === false) ||
    job?.isOffer === false
  )
}

export const isApplicationHistory = (job) =>
  isApplicationRejected(job) || isApplicationCompleted(job)

export const getNormalizedApplicationStatus = (job) => {
  return getBaseApplicationStatus(job)
}

const getRejectedStep = (job) => {
  const psychotestLink = String(
    job?.psychotestLink ?? job?.PsychotestLink ?? '',
  ).trim()
  const assessmentTestLink = String(
    job?.assessmentTestLink ?? job?.AssessmentTestLink ?? '',
  ).trim()
  const hasAssessmentStage = psychotestLink !== '' || assessmentTestLink !== ''
  const hasMedicalCheckUpStage =
    job?.jobIsMedicalCheckUp === true || job?.JobIsMedicalCheckUp === true

  if (isJobOfferExpired(job) || job?.isOffer === false) return 'Job Offer'
  if (hasMedicalCheckUpStage && job?.isMedicalCheckUp === false)
    return 'Medical Check Up'
  if (isInterviewExpired(job)) return getBaseApplicationStatus(job)
  if (job?.isInterviewUser === false) return 'Interview User'
  if (job?.isInterview === false) return 'Interview HC'
  if (
    hasAssessmentStage &&
    (isAssessmentExpired(job) || job?.isAssessment === false)
  )
    return 'Assessment'
  if (
    isPretestExpired(job) ||
    job?.isPassPretest === false ||
    job?.IsPassPretest === false ||
    job?.isScreening === false ||
    job?.isScreeningUser === false
  ) {
    return 'Screening'
  }

  if (job?.isOffer === false) return 'Job Offer'
  if (hasMedicalCheckUpStage && job?.isMedicalCheckUp === false)
    return 'Medical Check Up'
  if (job?.isInterviewUser === false) return 'Interview User'
  if (job?.isInterview === false) return 'Interview HC'
  if (hasAssessmentStage && job?.isAssessment === false) return 'Assessment'
  if (job?.isScreening === false || job?.isScreeningUser === false) {
    return 'Screening'
  }

  return ''
}

export const buildApplicationProgressSteps = (job, lang) => {
  const applicationStatus = getNormalizedApplicationStatus(job)
  const rejectedStep = getRejectedStep(job)
  const currentStep = rejectedStep || applicationStatus
  const isCompletedApplication = isApplicationCompleted(job)
  const psychotestLink = String(
    job?.psychotestLink ?? job?.PsychotestLink ?? '',
  ).trim()
  const assessmentTestLink = String(
    job?.assessmentTestLink ?? job?.AssessmentTestLink ?? '',
  ).trim()
  const hasMedicalCheckUpStage =
    job?.jobIsMedicalCheckUp === true || job?.JobIsMedicalCheckUp === true
  const applicationSteps = APPLICATION_STEPS.filter((step) => {
    if (
      step.key === 'Assessment' &&
      psychotestLink === '' &&
      assessmentTestLink === ''
    )
      return false

    if (step.key === 'Medical Check Up' && !hasMedicalCheckUpStage)
      return false

    return true
  })
  const currentIndex = applicationSteps.findIndex(
    (step) => step.key === currentStep,
  )

  return applicationSteps.map((step, index) => {
    let state = 'pending'

    if (rejectedStep) {
      if (index < currentIndex) {
        state = 'done'
      } else if (index === currentIndex) {
        state = 'rejected'
      }
    } else if (isCompletedApplication && index <= currentIndex) {
      state = 'done'
    } else if (index < currentIndex) {
      state = 'done'
    } else if (index === currentIndex) {
      state = currentStep === 'Job Offer' && job?.isOffer === true ? 'done' : 'active'
    }

    return {
      label: Lang(lang, step.labelKey),
      state,
      ...(STEP_PRESENTATION_MAP[state] || STEP_PRESENTATION_MAP.pending),
    }
  })
}

export const getApplicationStatusBadge = (job, lang) => {
  const applicationStatus = getNormalizedApplicationStatus(job)
  const hasRejectedStage = isApplicationRejected(job)
  const isCompletedApplication = isApplicationCompleted(job)
  const blueBadgeClassName =
    'inline-flex w-fit rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-[13px] py-[5px] text-[12px] font-medium leading-4 text-[#1D4ED8]'

  if (hasRejectedStage) {
    return {
      badgeLabel: Lang(lang, 'rejected'),
      badgeClassName: blueBadgeClassName,
    }
  }

  if (isCompletedApplication) {
    return {
      badgeLabel: Lang(lang, 'hired'),
      badgeClassName: blueBadgeClassName,
    }
  }

  const labelKey =
    applicationStatus === 'Screening'
      ? 'screening'
      : applicationStatus === 'Assessment'
        ? 'assessment'
        : applicationStatus === 'Interview HC'
          ? 'interview_hc'
          : applicationStatus === 'Interview User'
            ? 'interview_user'
            : applicationStatus === 'Medical Check Up'
              ? 'medical_check_up'
              : applicationStatus === 'Job Offer'
                ? 'job_offer'
                : 'applied'

  return {
    badgeLabel: Lang(lang, labelKey),
    badgeClassName: blueBadgeClassName,
  }
}
